import React, { createContext, useContext, ReactNode } from 'react';
import { useSupabaseData } from './SupabaseDataContext';
import { categoryApi } from '../lib/supabase-api';
import { Category } from '../types';

interface SupabaseAdminContextType {
  // Categories (Supabase connected)
  categories: Category[];
  flatCategories: Category[];
  loading: boolean;
  error: string | null;
  addCategory: (name: string, parentId?: number) => Promise<Category | null>;
  updateCategory: (id: number, updates: Partial<Category>) => Promise<Category | null>;
  deleteCategory: (id: number) => Promise<boolean>;
  getCategoryTree: () => Category[];
  getCategoryPath: (categoryId: number) => Category[];
}

const SupabaseAdminContext = createContext<SupabaseAdminContextType | undefined>(undefined);

interface SupabaseAdminProviderProps {
  children: ReactNode;
}

export const SupabaseAdminProvider: React.FC<SupabaseAdminProviderProps> = ({ children }) => {
  // Data sharing: get categories from SupabaseDataContext (prevents duplicate requests)
  const { categories, isLoading: loading, error } = useSupabaseData();
  
  // Category management functions (API calls only, local state updates via real-time subscriptions)
  const addCategory = async (name: string, parentId?: number): Promise<Category | null> => {
    try {
      return await categoryApi.create(name, parentId);
    } catch (err) {
      console.error('Error adding category:', err);
      return null;
    }
  };

  const updateCategory = async (id: number, updates: Partial<Category>): Promise<Category | null> => {
    try {
      return await categoryApi.update(id, updates);
    } catch (err) {
      console.error('Error updating category:', err);
      return null;
    }
  };

  const deleteCategory = async (id: number): Promise<boolean> => {
    try {
      return await categoryApi.delete(id);
    } catch (err) {
      console.error('Error deleting category:', err);
      return false;
    }
  };

  // Utility functions
  const getCategoryOrder = (categoryName: string, level: number): number => {
    const orderMap: { [key: string]: number } = {
      'sexual-health': 1, 'hair-loss': 2, 'skin-care': 3, 'weight-management': 4, 'beauty-skincare': 5, 'heart-health': 6,
      // Level 1 categories
      'sildenafil': 1, 'tadalafil': 2, 'vardenafil': 3, 'avanafil': 4,
      'dapoxetine': 5, 'sildenafil-delayed': 6, 'tadalafil-delayed': 7,
      'finasteride': 1, 'dutasteride': 2, 'minoxidil': 3,
      'roaccutane': 1, 'doxycycline': 2, 'tretinoin': 3,
      'rybelsus': 1, 'fat-blocker': 2, 'carb-blocker': 3,
      'melasma-whitening': 1, 'hydroquinone': 2, 'eyelash-growth': 3
    };
    return orderMap[categoryName] || 999;
  };

  const getCategoryTree = (): Category[] => {
    const buildTree = (parentId?: number): Category[] => {
      return categories
        .filter(cat => cat.parentId === parentId)
        .map(cat => ({
          ...cat,
          children: buildTree(cat.id)
        }))
        .sort((a, b) => getCategoryOrder(a.name, a.level) - getCategoryOrder(b.name, b.level));
    };
    return buildTree();
  };

  const getCategoryPath = (categoryId: number): Category[] => {
    const path: Category[] = [];
    let currentId: number | undefined = categoryId;

    while (currentId) {
      const category = categories.find(c => c.id === currentId);
      if (category) {
        path.unshift(category);
        currentId = category.parentId;
      } else {
        currentId = undefined;
      }
    }
    return path;
  };

  const getSortedFlatCategories = (): Category[] => {
    return categories.sort((a, b) => {
      if (a.level !== b.level) {
        return a.level - b.level;
      }
      return getCategoryOrder(a.name, a.level) - getCategoryOrder(b.name, b.level);
    });
  };

  const contextValue: SupabaseAdminContextType = {
    categories: getCategoryTree(), // Hierarchical structure
    flatCategories: getSortedFlatCategories(),
    loading,
    error,
    addCategory,
    updateCategory,
    deleteCategory,
    getCategoryTree,
    getCategoryPath
  };

  return (
    <SupabaseAdminContext.Provider value={contextValue}>
      {children}
    </SupabaseAdminContext.Provider>
  );
};

export const useSupabaseAdmin = (): SupabaseAdminContextType => {
  const context = useContext(SupabaseAdminContext);
  if (!context) {
    throw new Error('useSupabaseAdmin must be used within a SupabaseAdminProvider');
  }
  return context;
};